import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_SURVEY_FOR_RESPONSE,
  GET_MY_SURVEY_RESPONSE,
  START_SURVEY_RESPONSE,
  SUBMIT_SURVEY_ANSWER,
  SAVE_SURVEY_PROGRESS,
  COMPLETE_SURVEY_RESPONSE,
  VERIFY_SURVEY_MAGIC_LINK,
  SUBMIT_SURVEY_TERRITORIO_ANSWERS,
  GET_AVAILABLE_SURVEYS_DIPENDENTE,
  GET_COMPILATO_DA_SONDAGGIO
} from '../services/surveyQueries';
import { UserRound } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { surveyOpContext, dimenticaLinkToken, codiceErrore, getSurveyClient, percorsiArea, AreaSondaggi } from '../services/surveyAuth';
import { removeRepresentativeToken } from '../services/representativeService';
import TerritorioQuestion, { TerritorioRispondente, TERRITORIO_ESEMPIO } from '../components/survey/TerritorioQuestion';
import { mappaRisposteConTerritorio } from '../utils/surveyAnswerFormat';
import { eDipendente } from '../utils/sezioniDipendente';
import type { AssociateData } from '../services/otpAuthService';

const UPLOAD_URL = process.env.REACT_APP_UPLOAD_URL || 'http://localhost:5000/uploads';

// Fisher-Yates shuffle (seed-based: usa surveyId per consistenza nella sessione)
function seededShuffle<T>(arr: T[], seed: string): T[] {
  const copy = [...arr];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  for (let i = copy.length - 1; i > 0; i--) {
    hash = ((hash * 1664525) + 1013904223) | 0;
    const j = Math.abs(hash) % (i + 1);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface QuestionOption { optionId: string; text: string; value: string; }
interface MediaContent { type: 'image' | 'pdf' | null; url: string; filename?: string; caption?: string; }
interface ScaleConfig { min: number; max: number; minLabel?: string; maxLabel?: string; }
interface MatrixRow { rowId: string; text: string; }
interface MatrixColumn { colId: string; text: string; value: string; }
interface MatrixConfig { rows: MatrixRow[]; columns: MatrixColumn[]; }
interface SliderConfig { min: number; max: number; step: number; minLabel: string; maxLabel: string; showValue: boolean; }

interface BranchingRule {
  condition: { operator: string; value: string; };
  action: { type: string; targetId: string; };
}

interface ShowIfCondition {
  questionId: string;
  operator: string;
  value: string;
  action?: 'show' | 'hide';
}

interface Question {
  questionId: string; order: number; type: string; text: string; description: string;
  required: boolean; options: QuestionOption[]; scaleConfig?: ScaleConfig;
  matrixConfig?: MatrixConfig; sliderConfig?: SliderConfig; media?: MediaContent;
  placeholder?: string;
  territorioConfig?: { livello: string; tipoRisposta?: string; obbligaTutteLeProvince: boolean } | null;
  branching?: BranchingRule[];
  showIf?: ShowIfCondition;
  validation?: { minLength?: number; maxLength?: number; minValue?: number; maxValue?: number; minSelections?: number; maxSelections?: number; pattern?: string; };
}

interface Step { stepId: string; order: number; title: string; description: string; media?: MediaContent; questions: Question[]; }

interface Survey {
  id: string; title: string; description: string; status: string;
  startDate?: string | null; endDate?: string | null;
  identitaVerificata?: boolean;
  settings: {
    showProgressBar: boolean; requireAllQuestions: boolean; randomizeQuestions: boolean;
    thankYouMessage: string; redirectUrl: string; welcomeMessage?: string;
    closedMessage?: string; allowSaveDraft?: boolean; timeLimitMinutes?: number; maxResponses?: number;
  };
  steps: Step[];
}

interface Answer {
  questionId: string; value: string;
  // Domanda «Territorio di competenza»: una voce per provincia
  territorio?: { livello: string; regione?: string | null; provincia: string } | null;
}

// Errori del server che fermano la compilazione (si mostra il messaggio del server)
const CODICI_IDENTITA = ['IDENTITA_RICHIESTA', 'LINK_NON_VALIDO'];
// Area associativa: carica non abilitata ai sondaggi (si torna alla dashboard)
const CODICI_RAPPRESENTANTE = ['RAPPRESENTANTE_SENZA_PERMESSO'];
// Sondaggio di un altro pubblico: il pulsante porta all'area da cui si compila
const AZIONI_PUBBLICO: Record<string, { etichetta: string; percorso: string }> = {
  PUBBLICO_STUDI: { etichetta: 'Vai all\'area riservata', percorso: percorsiArea('riservata').elenco },
  PUBBLICO_RAPPRESENTANTI: { etichetta: 'Vai all\'area associativa', percorso: percorsiArea('associativa').elenco },
};
// Dipendente dello studio: risponde il titolare (sola lettura) o sezione Sondaggi non abilitata
const CODICI_DIPENDENTE = ['RISPONDE_IL_TITOLARE', 'SEZIONE_NON_ABILITATA'];
const CODICI_BLOCCO = ['IMPERSONIFICAZIONE', 'TERRITORIO_NON_DISPONIBILE', ...CODICI_RAPPRESENTANTE, ...CODICI_DIPENDENTE, ...Object.keys(AZIONI_PUBBLICO)];
const MESSAGGIO_SOLO_TITOLARE = 'Per questo sondaggio risponde il titolare dello studio. Quando l\'avrà inviato potrai vedere le risposte.';

// Chi ha compilato la risposta dello studio (campo `compilatoDa` del server)
interface CompilatoDa { tipo?: string | null; dipendenteId?: string | null; nome?: string | null }

const dataLunga = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
};

// Solo le risposte delle province del territorio (le altre il server le rifiuterebbe)
const filtraProvince = (risposte: Record<string, string>, province: string[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const p of province) if (risposte[p]) out[p] = risposte[p];
  return out;
};

// 401 del contesto GraphQL: token associato scaduto o non più valido
const bearerRifiutato = (error: unknown): boolean =>
  (error as { networkError?: { statusCode?: number } } | null)?.networkError?.statusCode === 401;

interface SurveyResponsePageProps {
  // 'associativa': sondaggi per i rappresentanti (Bearer rappresentante o link firmato per
  // rappresentante); pagina dentro la shell o sulla pagina pubblica del link
  area?: AreaSondaggi;
}

const SurveyResponsePage: React.FC<SurveyResponsePageProps> = ({ area = 'riservata' }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const associativa = area === 'associativa';
  const percorsi = percorsiArea(area);
  // Nell'area associativa la pagina sta nella shell o sotto la testata del link: niente altezza a tutto schermo
  const classeContenitore = associativa ? 'min-h-[60vh]' : 'min-h-screen';
  const toast = useToast();
  const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const getPartitaIVAFromStorage = () => {
    try { return JSON.parse(localStorage.getItem('associate_data') || '{}').partitaIVA || ''; } catch { return ''; }
  };
  // Area associativa: la P.IVA non viene dal client (la risposta è del rappresentante)
  const [partitaIVA, setPartitaIVA] = useState<string>(associativa ? '' : getPartitaIVAFromStorage());
  // Chi è entrato nell'area riservata: il titolare o un dipendente dello studio (spec dipendenti §8)
  const [datiSessione] = useState<AssociateData | null>(() => {
    if (associativa) return null;
    try { return JSON.parse(localStorage.getItem('associate_data') || 'null'); } catch { return null; }
  });
  const dipendente = eDipendente(datiSessione);

  const { data: surveyData, loading: surveyLoading } = useQuery(GET_SURVEY_FOR_RESPONSE, {
    variables: { id }, skip: !id
  });
  const survey: Survey | null = surveyData?.survey || null;
  // Sondaggi con identità verificata (i nuovi): Bearer associato o link firmato.
  // Gli altri (vecchi) restano come prima: solo P.IVA, nessun Bearer.
  const verificato = survey?.identitaVerificata === true;

  // Identità dei sondaggi verificati: Bearer associato e/o link firmato ricevuto
  // per email (`#lt=<token>`, letto una volta e tolto dall'indirizzo). Il server
  // decide: con il Bearer vale il Bearer, altrimenti il link; la P.IVA non conta.
  // Area associativa: allo stesso modo Bearer rappresentante e/o link per rappresentante
  const [identitaIniziale] = useState(() => (isPreview || !id ? {} : surveyOpContext(id, { area })));
  // Bearer rifiutato dal server (401: sessione non più valida) → si prosegue senza
  // (link se c'è, altrimenti schermata di accesso)
  const [opHeaders, setOpHeaders] = useState(identitaIniziale.headers);
  const [linkToken, setLinkToken] = useState<string | null>(identitaIniziale.linkToken || null);
  // Schermata «accedi all'area riservata o usa il link»; linkScaduto se il link non vale più
  const [identitaMancante, setIdentitaMancante] = useState<{ linkScaduto: boolean } | null>(null);
  // Compilazione impossibile (territorio non disponibile, impersonificazione, altro pubblico): messaggio del server
  const [bloccoServer, setBloccoServer] = useState<string | null>(null);
  // Codice del blocco: decide il pulsante (dashboard, area dell'altro pubblico, elenco)
  const [bloccoCodice, setBloccoCodice] = useState<string | null>(null);

  // Il magic link storico (?t=) vale solo nell'area riservata
  const magicToken = associativa ? '' : searchParams.get('t') || '';
  // Il link firmato si verifica anche da loggati (scaduto → invito ad accedere);
  // il magic link storico (?t=) solo senza P.IVA, come sempre.
  const needsMagicLinkVerify = !isPreview && (!!linkToken || (!!magicToken && !partitaIVA));

  // Magic-link: se l'utente arriva da email (?t=<jwt>) e non e' loggato, scopri la P.IVA
  // dal token firmato server-side. Una volta ottenuta la P.IVA il flusso prosegue identico
  // al path "loggato": la sicurezza vera resta canAccessSurvey su startSurveyResponse.
  const { data: magicLinkData, loading: magicLinkLoading, error: magicLinkError } = useQuery(VERIFY_SURVEY_MAGIC_LINK, {
    variables: { surveyId: id, token: linkToken || magicToken },
    skip: !needsMagicLinkVerify || !id,
    fetchPolicy: 'network-only'
  });

  // Errori del server su identità e territorio. true = gestito (niente toast generico).
  // `mutation`: l'operazione non si ripete da sola, l'utente va avvisato.
  const gestisciErroreBloccante = (error: unknown, { mutation = false }: { mutation?: boolean } = {}): boolean => {
    if (bearerRifiutato(error)) {
      // Area associativa: il token rifiutato si toglie, così RepresentativePrivateRoute
      // non considera più collegato il rappresentante (come AssociativeSurveys)
      if (associativa) removeRepresentativeToken();
      if (opHeaders) setOpHeaders(undefined);
      if (mutation) toast.error('Sessione scaduta, riprova');
      return true;
    }
    const code = codiceErrore(error);
    if (code && CODICI_IDENTITA.includes(code)) {
      if (code === 'LINK_NON_VALIDO' && id) {
        dimenticaLinkToken(id);
        setLinkToken(null);
      }
      setIdentitaMancante(prev => prev || { linkScaduto: code === 'LINK_NON_VALIDO' });
      return true;
    }
    if (code && CODICI_BLOCCO.includes(code)) {
      setBloccoCodice(code);
      setBloccoServer((error as Error)?.message || 'Questo sondaggio non è disponibile');
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!magicLinkData?.verifySurveyMagicLink) return;
    const res = magicLinkData.verifySurveyMagicLink;
    if (res.valid) {
      // Magic link storico (sondaggi vecchi): la P.IVA guida la compilazione come sempre.
      // Link firmato (sondaggi verificati, anche quello per rappresentante senza P.IVA):
      // l'identità è il link stesso, la P.IVA non serve.
      if (!res.identitaVerificata && res.partitaIVA) setPartitaIVA(res.partitaIVA);
    } else if (res.linkFirmato && linkToken) {
      // Link firmato scaduto o non valido: da loggati si prosegue con il Bearer
      if (id) dimenticaLinkToken(id);
      setLinkToken(null);
      if (!opHeaders) setIdentitaMancante({ linkScaduto: true });
    } else if (res.error) {
      toast.error('Link non valido', `${res.error}. Effettua l'accesso dall'area associati per compilare il sondaggio.`);
    }
  }, [magicLinkData]); // eslint-disable-line

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [responseId, setResponseId] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  // Risposta dello studio già inviata prima di aprire la pagina (da chiunque dello studio)
  const [completataPrima, setCompletataPrima] = useState(false);
  // Al passaggio alla schermata «Grazie» si torna in cima: dopo una lunga lista di province la pagina
  // resterebbe scorsa in fondo e il messaggio non si vedrebbe.
  useEffect(() => {
    if (isCompleted) window.scrollTo({ top: 0, behavior: 'auto' });
  }, [isCompleted]);
  const [showWelcome, setShowWelcome] = useState(true);          // welcomeMessage
  const [timeLeft, setTimeLeft] = useState<number | null>(null); // timeLimitMinutes
  const [timeExpired, setTimeExpired] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  // randomizeQuestions: ordine fisso per sessione (ricalcolato al mount)
  const [shuffledSteps, setShuffledSteps] = useState<Step[] | null>(null);
  const [stepHistory, setStepHistory] = useState<number[]>([0]); // cronologia navigazione per branching
  // Domanda «Territorio di competenza»: territorio deciso dal server e risposta per provincia
  const [territorioRispondente, setTerritorioRispondente] = useState<TerritorioRispondente | null>(null);
  const [territorioAnswers, setTerritorioAnswers] = useState<Record<string, string>>({});
  const territorioTimer = useRef<NodeJS.Timeout | null>(null);
  const territorioInSospeso = useRef<{ questionId: string; risposte: Record<string, string> } | null>(null);
  // La risposta in corso si riallinea al territorio attuale con startSurveyResponse (una volta)
  const territorioRiallineato = useRef(false);
  const catenaTerritorio = useRef<Promise<boolean>>(Promise.resolve(true));
  // Territorio più recente per il salvataggio (le callback possono venire da un render vecchio)
  const territorioRef = useRef<TerritorioRispondente | null>(null);
  territorioRef.current = territorioRispondente;
  // La risposta esistente si ripristina una volta sola, non a ogni trasmissione dalla cache
  const ripristinato = useRef(false);

  // Operazioni di risposta: client dedicato. Sui sondaggi verificati il Bearer
  // associato nel context e il link come variabile `linkToken`; sugli altri la P.IVA.
  const surveyClient = getSurveyClient();
  // Area associativa: Bearer rappresentante e/o link anche sui sondaggi non verificati,
  // mai la P.IVA (decide il server)
  const conBearer = verificato || associativa;
  // Il dipendente manda il suo Bearer anche sui sondaggi vecchi (identità sempre per P.IVA):
  // così il server sa che non è il titolare
  const opContext = (conBearer || dipendente) && opHeaders ? { headers: opHeaders } : undefined;
  const identitaVars = conBearer
    ? { partitaIVA: null, linkToken: linkToken || null }
    : { partitaIVA: partitaIVA || null, linkToken: null };
  const verificaLinkInCorso = needsMagicLinkVerify && !magicLinkData && !magicLinkError;
  // Il dipendente risponde solo con il suo Bearer valido: senza (token scaduto), con la P.IVA dello
  // studio risponderebbe come il titolare
  const haIdentita = conBearer ? (!!opHeaders || !!linkToken) : dipendente ? !!opHeaders : !!partitaIVA;
  // Sondaggio verificato (o area associativa, o dipendente) senza Bearer né link valido: schermata di accesso subito
  const identitaAssente = !isPreview && !!survey && (conBearer || dipendente) && !haIdentita && !verificaLinkInCorso;
  const schermataAccesso = identitaMancante || (identitaAssente ? { linkScaduto: false } : null);

  // Dipendente: l'elenco dei suoi sondaggi dice se risponde solo il titolare (prima di avviare la risposta)
  const { data: elencoDipendente, loading: elencoDipendenteLoading } = useQuery(GET_AVAILABLE_SURVEYS_DIPENDENTE, {
    client: surveyClient,
    context: opHeaders ? { headers: opHeaders } : undefined,
    variables: { partitaIVA: datiSessione?.partitaIVA || '' },
    skip: isPreview || !dipendente || !id || !datiSessione?.partitaIVA || !opHeaders,
    fetchPolicy: 'network-only'
  });
  const soloTitolare = !isPreview && dipendente && !!(elencoDipendente?.availableSurveys || [])
    .find((s: { id: string; rispondeSoloTitolare?: boolean | null }) => s.id === id)?.rispondeSoloTitolare;
  const attesaElencoDipendente = !isPreview && dipendente && !!datiSessione?.partitaIVA && !!opHeaders && elencoDipendenteLoading && !elencoDipendente;

  const { data: responseData, loading: responseLoading, error: responseError } = useQuery(GET_MY_SURVEY_RESPONSE, {
    client: surveyClient,
    context: opContext,
    variables: { surveyId: id, ...identitaVars },
    skip: isPreview || !id || !survey || verificaLinkInCorso || !haIdentita || !!identitaMancante || !!bloccoServer,
    fetchPolicy: 'network-only'
  });
  const erroreRispostaBloccante = !!responseError && (
    bearerRifiutato(responseError) || [...CODICI_IDENTITA, ...CODICI_BLOCCO].includes(codiceErrore(responseError) || '')
  );

  useEffect(() => {
    if (responseError) gestisciErroreBloccante(responseError);
  }, [responseError]); // eslint-disable-line

  // Chi ha compilato la risposta esistente: query a parte, un errore (backend senza il campo) si ignora
  const { data: compilatoDaData } = useQuery(GET_COMPILATO_DA_SONDAGGIO, {
    client: surveyClient,
    context: opContext,
    variables: { surveyId: id, ...identitaVars },
    skip: associativa || isPreview || !responseData?.mySurveyResponse?.id,
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore'
  });
  const compilatoDa: CompilatoDa | null = compilatoDaData?.mySurveyResponse?.compilatoDa || null;
  const compilatoDaMe = (c: CompilatoDa): boolean => {
    if (c.tipo === 'titolare') return !dipendente;
    if (c.tipo === 'dipendente') return dipendente && !!c.dipendenteId && c.dipendenteId === datiSessione?.dipendente?.id;
    return false;
  };
  const avvisoInCompilazione = compilatoDa?.nome && responseData?.mySurveyResponse?.status === 'IN_PROGRESS' && !compilatoDaMe(compilatoDa)
    ? compilatoDa.nome
    : null;

  const [startResponse] = useMutation(START_SURVEY_RESPONSE, {
    client: surveyClient,
    context: opContext,
    onCompleted: (data) => {
      setResponseId(data.startSurveyResponse.id);
      setCurrentStepIndex(data.startSurveyResponse.currentStepIndex || 0);
      // Territorio ricalcolato dal server: le risposte fuori territorio si tolgono
      // (effetto su territorioRispondente) e si risalvano
      const terr = data.startSurveyResponse.respondent?.territorio;
      if (terr) setTerritorioRispondente(terr);
    },
    onError: (error) => {
      if (!gestisciErroreBloccante(error, { mutation: true })) toast.error('Errore', error.message);
    }
  });
  const [submitAnswer] = useMutation(SUBMIT_SURVEY_ANSWER, { client: surveyClient, context: opContext });
  const [submitTerritorio] = useMutation(SUBMIT_SURVEY_TERRITORIO_ANSWERS, { client: surveyClient, context: opContext });
  const [saveProgress] = useMutation(SAVE_SURVEY_PROGRESS, { client: surveyClient, context: opContext });
  const [completeResponse] = useMutation(COMPLETE_SURVEY_RESPONSE, {
    client: surveyClient,
    context: opContext,
    onCompleted: () => setIsCompleted(true),
    onError: (error) => {
      if (!gestisciErroreBloccante(error, { mutation: true })) toast.error('Errore', error.message);
    }
  });

  const loading = surveyLoading || magicLinkLoading || (!isPreview && responseLoading);
  const domandaTerritorio: Question | undefined = survey?.steps.flatMap(s => s.questions).find(q => q.type === 'territorio');

  // Inizializza shuffledSteps quando il sondaggio è caricato
  useEffect(() => {
    if (!survey) return;
    if (survey.settings.randomizeQuestions) {
      const shuffled = survey.steps.map(step => ({
        ...step,
        questions: seededShuffle(step.questions, survey.id + step.stepId)
      }));
      setShuffledSteps(shuffled);
    } else {
      setShuffledSteps(null);
    }
    // Se NON c'è welcomeMessage, salta la schermata di benvenuto
    if (!survey.settings.welcomeMessage) {
      setShowWelcome(false);
    }
  }, [survey?.id]); // eslint-disable-line

  // Inizializza timer
  useEffect(() => {
    if (survey?.settings.timeLimitMinutes && !isCompleted && !showWelcome) {
      setTimeLeft(survey.settings.timeLimitMinutes * 60);
    }
  }, [survey?.settings.timeLimitMinutes, showWelcome]); // eslint-disable-line

  // Countdown timer
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          setTimeExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft !== null && timeLeft > 0]); // eslint-disable-line

  // Auto-submit quando tempo scaduto
  useEffect(() => {
    if (timeExpired && responseId && !isCompleted) {
      toast.error('Tempo scaduto', 'Il tempo a disposizione è terminato. Il sondaggio verrà inviato automaticamente.');
      setTimeout(async () => {
        await salvaTerritorio();
        completeResponse({ variables: { responseId, ...identitaVars } });
      }, 2000);
    }
  }, [timeExpired]); // eslint-disable-line

  // Ripristina risposta esistente (non in preview)
  useEffect(() => {
    if (isPreview || ripristinato.current) return;
    if (responseData?.mySurveyResponse) {
      ripristinato.current = true;
      const resp = responseData.mySurveyResponse;
      setResponseId(resp.id);
      setCurrentStepIndex(resp.currentStepIndex || 0);
      if (resp.status === 'COMPLETED') {
        setIsCompleted(true);
        setCompletataPrima(true);
      }
      const existingAnswers: Record<string, string> = {};
      const existingTerritorio: Record<string, string> = {};
      resp.answers?.forEach((a: Answer) => {
        // Domanda territoriale: una voce per provincia, fuori dalla mappa delle risposte
        if (a.territorio?.provincia) existingTerritorio[a.territorio.provincia] = a.value;
        else existingAnswers[a.questionId] = a.value;
      });
      setTerritorioAnswers(existingTerritorio);
      if (resp.respondent?.territorio) setTerritorioRispondente(resp.respondent.territorio);
      // Pulisci risposte di domande che ora sono nascoste dalla logica condizionale
      setAnswers(clearHiddenAnswers(existingAnswers, existingTerritorio));
    }
  }, [responseData]); // eslint-disable-line

  // Risposta in corso con domanda territoriale: startSurveyResponse restituisce la
  // stessa risposta con il territorio ricalcolato dal server (la scheda può essere cambiata)
  useEffect(() => {
    if (isPreview || territorioRiallineato.current) return;
    const resp = responseData?.mySurveyResponse;
    if (!survey || !domandaTerritorio || !resp || resp.status !== 'IN_PROGRESS') return;
    if (soloTitolare || attesaElencoDipendente) return;
    // Sondaggio chiuso o fuori date: la pagina mostra già la schermata, nessuna chiamata
    const ora = new Date();
    if (survey.status === 'CLOSED' || (survey.endDate && new Date(survey.endDate) < ora) || (survey.startDate && new Date(survey.startDate) > ora)) return;
    territorioRiallineato.current = true;
    startResponse({ variables: { surveyId: id, ...identitaVars } });
  }, [survey, responseData, isPreview, soloTitolare, attesaElencoDipendente]); // eslint-disable-line

  // Territorio ristretto (es. una provincia tolta dalla scheda): via le risposte fuori
  // territorio e nuovo salvataggio, così il server sostituisce le voci vecchie
  useEffect(() => {
    if (isPreview || !territorioRispondente) return;
    const filtrate = filtraProvince(territorioAnswers, territorioRispondente.province);
    if (Object.keys(filtrate).length === Object.keys(territorioAnswers).length) return;
    setTerritorioAnswers(filtrate);
    riallineaDipendentiDelTerritorio(filtrate);
    if (domandaTerritorio && responseId) {
      territorioInSospeso.current = { questionId: domandaTerritorio.questionId, risposte: filtrate };
      salvaTerritorio();
    }
  }, [territorioRispondente]); // eslint-disable-line

  // Ricalcola visibilità quando il survey viene caricato (per risposte ripristinate)
  useEffect(() => {
    if (survey && Object.keys(answers).length > 0) {
      setAnswers(prev => clearHiddenAnswers(prev));
    }
  }, [survey]); // eslint-disable-line

  // Avvia nuova risposta (solo dopo che la query GET_MY_SURVEY_RESPONSE ha finito, non in preview)
  useEffect(() => {
    if (isPreview) return;
    if (verificaLinkInCorso || identitaMancante || bloccoServer || erroreRispostaBloccante) return;
    // Dipendente: prima l'elenco (risponde solo il titolare?), poi eventualmente la risposta
    if (soloTitolare || attesaElencoDipendente) return;
    if (survey && !responseLoading && !responseData?.mySurveyResponse && !responseId && haIdentita) {
      startResponse({ variables: { surveyId: id, ...identitaVars } });
    }
  }, [survey, responseLoading, responseData, responseId, partitaIVA, linkToken, opHeaders, isPreview, verificaLinkInCorso, identitaMancante, bloccoServer, erroreRispostaBloccante, soloTitolare, attesaElencoDipendente]); // eslint-disable-line

  const displayedSteps = shuffledSteps || survey?.steps || [];
  const currentStep = displayedSteps[currentStepIndex];
  const totalSteps = displayedSteps.length;
  const progress = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;

  const submitAnswerToBackend = async (questionId: string, value: string) => {
    if (isPreview) return; // In preview non salva nulla
    if (responseId) {
      // Sessione scaduta (401) o identità: avviso e schermata di accesso; gli altri errori come prima
      try { await submitAnswer({ variables: { responseId, questionId, value, ...identitaVars } }); } catch (error) {
        gestisciErroreBloccante(error, { mutation: true });
      }
    }
  };

  // ── Domanda territoriale: salvataggio con debounce di TUTTE le province compilate ──
  // Un salvataggio alla volta, in ordine: il server sostituisce in blocco le voci
  // della domanda, una richiesta vecchia arrivata dopo cancellerebbe quella nuova.
  const salvaTerritorio = (): Promise<boolean> => {
    if (territorioTimer.current) {
      clearTimeout(territorioTimer.current);
      territorioTimer.current = null;
    }
    const esegui = async (): Promise<boolean> => {
      const pendente = territorioInSospeso.current;
      if (isPreview || !pendente || !responseId) return true;
      territorioInSospeso.current = null;
      // Solo province del territorio attuale (ristretto nel frattempo: le altre si tolgono)
      const terr = territorioRef.current;
      const valide = terr ? filtraProvince(pendente.risposte, terr.province) : pendente.risposte;
      const risposte = Object.entries(valide)
        .filter(([, value]) => !!value)
        .map(([provincia, value]) => ({ provincia, value }));
      try {
        const { data } = await submitTerritorio({
          variables: { responseId, questionId: pendente.questionId, risposte, linkToken: linkToken || null }
        });
        const terr = data?.submitSurveyTerritorioAnswers?.respondent?.territorio;
        if (terr) setTerritorioRispondente(terr);
        return true;
      } catch (error) {
        if (!gestisciErroreBloccante(error, { mutation: true })) toast.error('Risposta non salvata', (error as Error)?.message || 'Riprova');
        return false;
      }
    };
    const prossimo = catenaTerritorio.current.then(esegui, esegui);
    catenaTerritorio.current = prossimo;
    return prossimo;
  };

  // Territorio da mostrare: quello del server; in anteprima l'esempio (Abruzzo)
  const territorioDi = (question: Question): TerritorioRispondente | null =>
    isPreview ? TERRITORIO_ESEMPIO(question.territorioConfig?.livello || 'regionale') : territorioRispondente;

  const handleTerritorioChange = (questionId: string, provincia: string, value: string) => {
    const nuove = { ...territorioAnswers, [provincia]: value };
    setTerritorioAnswers(nuove);
    riallineaDipendentiDelTerritorio(nuove);
    if (isPreview) return; // In preview non salva nulla
    territorioInSospeso.current = { questionId, risposte: nuove };
    if (territorioTimer.current) clearTimeout(territorioTimer.current);
    territorioTimer.current = setTimeout(() => { salvaTerritorio(); }, 400);
  };

  // Le condizioni «Mostra se» possono dipendere dalla domanda territoriale: cambiate
  // le province, le risposte delle domande ora nascoste si svuotano (anche sul server)
  const riallineaDipendentiDelTerritorio = (nuoveTerritorio: Record<string, string>) => {
    const cleared = clearHiddenAnswers(answers, nuoveTerritorio);
    const svuotate = Object.keys(answers).filter(qId => answers[qId] && cleared[qId] === '');
    if (svuotate.length === 0) return;
    setAnswers(cleared);
    svuotate.forEach(qId => submitAnswerToBackend(qId, ''));
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    const newAnswers = { ...answers, [questionId]: value };
    const cleared = clearHiddenAnswers(newAnswers);
    setAnswers(cleared);
    // Invia al backend la risposta principale
    submitAnswerToBackend(questionId, value);
    // Invia cancellazione per ogni domanda che aveva un valore e ora è vuota
    for (const qId of Object.keys(newAnswers)) {
      if (qId !== questionId && newAnswers[qId] && newAnswers[qId] !== '' && cleared[qId] === '') {
        submitAnswerToBackend(qId, '');
      }
    }
  };

  const handleAnswerChangeDebounced = (questionId: string, value: string) => {
    const newAnswers = { ...answers, [questionId]: value };
    const cleared = clearHiddenAnswers(newAnswers);
    setAnswers(cleared);
    if (debounceTimers.current[questionId]) clearTimeout(debounceTimers.current[questionId]);
    debounceTimers.current[questionId] = setTimeout(() => {
      submitAnswerToBackend(questionId, value);
      for (const qId of Object.keys(newAnswers)) {
        if (qId !== questionId && newAnswers[qId] && newAnswers[qId] !== '' && cleared[qId] === '') {
          submitAnswerToBackend(qId, '');
        }
      }
    }, 800);
  };

  // ── Branching engine ──
  const evaluateBranchingCondition = (operator: string, answerValue: string, conditionValue: string): boolean => {
    const answer = (answerValue || '').trim();
    const condition = (conditionValue || '').trim();

    switch (operator) {
      case 'equals': return answer === condition;
      case 'not_equals': return answer !== condition;
      case 'contains': {
        // Per checkbox/multi-select: verifica se il valore è tra le risposte separate da virgola
        const parts = answer.split(',').map(v => v.trim());
        return parts.includes(condition) || answer.includes(condition);
      }
      case 'not_contains': {
        const parts = answer.split(',').map(v => v.trim());
        return !parts.includes(condition) && !answer.includes(condition);
      }
      case 'greater_than': return parseFloat(answer) > parseFloat(condition);
      case 'less_than': return parseFloat(answer) < parseFloat(condition);
      case 'is_empty': return !answer;
      case 'is_not_empty': return !!answer;
      default: return false;
    }
  };

  const evaluateStepBranching = (): { type: string; targetId: string } | null => {
    if (!currentStep) return null;
    for (const question of currentStep.questions) {
      if (!question.branching?.length) continue;
      const answerValue = risposteConTerritorio(answers)[question.questionId] || '';
      for (const rule of question.branching) {
        if (evaluateBranchingCondition(rule.condition.operator, answerValue, rule.condition.value)) {
          return rule.action;
        }
      }
    }
    return null;
  };

  // Risposte + domanda territoriale (province unite dalla virgola, come il server).
  // Se la domanda territoriale è a sua volta nascosta, per le altre vale vuota.
  const risposteConTerritorio = (
    answersMap: Record<string, string>,
    terr: Record<string, string> = territorioAnswers
  ): Record<string, string> => {
    const mappa = mappaRisposteConTerritorio(answersMap, domandaTerritorio?.questionId, terr);
    if (domandaTerritorio?.showIf && !isQuestionVisibleWith(domandaTerritorio, mappa)) {
      return { ...mappa, [domandaTerritorio.questionId]: '' };
    }
    return mappa;
  };

  // Visibilità condizionale: verifica se una domanda deve essere mostrata
  const isQuestionVisible = (question: Question): boolean =>
    isQuestionVisibleWith(question, risposteConTerritorio(answers));

  // Verifica visibilità con un set di risposte arbitrario (per pulizia cascata)
  const isQuestionVisibleWith = (question: Question, answersMap: Record<string, string>): boolean => {
    if (!question.showIf) return true;
    const { questionId: depId, operator, value: condValue, action = 'show' } = question.showIf;
    const depAnswer = answersMap[depId] || '';
    const conditionMet = evaluateBranchingCondition(operator, depAnswer, condValue || '');
    return action === 'hide' ? !conditionMet : conditionMet;
  };

  // Cancella risposte delle domande nascoste a cascata (pura, senza side effects)
  // La domanda territoriale entra nel calcolo (le sue risposte stanno in territorioAnswers)
  // ma non nel risultato.
  const clearHiddenAnswers = (
    updatedAnswers: Record<string, string>,
    terr: Record<string, string> = territorioAnswers
  ): Record<string, string> => {
    if (!survey) return updatedAnswers;
    const steps = shuffledSteps || survey.steps;
    const allQuestions = steps.flatMap(s => s.questions);
    let changed = true;
    const idTerritorio = domandaTerritorio?.questionId;
    // Copia: la cascata modifica `result`, mai le risposte ricevute
    const result = { ...mappaRisposteConTerritorio(updatedAnswers, idTerritorio, terr) };

    while (changed) {
      changed = false;
      for (const q of allQuestions) {
        if (!q.showIf) continue;
        if (!(q.questionId in result) || result[q.questionId] === '') continue;

        if (!isQuestionVisibleWith(q, result)) {
          result[q.questionId] = '';
          changed = true;
        }
      }
    }
    if (idTerritorio && !(idTerritorio in updatedAnswers)) delete result[idTerritorio];
    return result;
  };

  const validateStep = (): boolean => {
    if (!currentStep) return true;
    for (const question of currentStep.questions) {
      if (question.type === 'html') continue;
      if (!isQuestionVisible(question)) continue;

      // Domanda territoriale: tutte le province con obbligaTutteLeProvince, altrimenti almeno una;
      // non obbligatoria (required false) può restare vuota (stesse regole del server al completamento)
      if (question.type === 'territorio') {
        const province = territorioDi(question)?.province || [];
        if (province.length === 0) {
          toast.error('Territorio non disponibile', 'Il territorio di competenza non è ancora stato caricato: riprova tra qualche istante');
          return false;
        }
        const mancanti = province.filter(p => !territorioAnswers[p]);
        if (question.required === false && mancanti.length === province.length) continue;
        if (question.territorioConfig?.obbligaTutteLeProvince !== false && mancanti.length > 0) {
          toast.error('Risposte mancanti', `Rispondi per tutte le province: ${mancanti.join(', ')}`);
          return false;
        }
        if (mancanti.length === province.length) {
          toast.error('Risposte mancanti', 'Rispondi per almeno una provincia');
          return false;
        }
        continue;
      }

      const answer = answers[question.questionId] || '';
      const v = question.validation;

      // Required check
      if (survey?.settings.requireAllQuestions && question.required && !answer) {
        toast.error('Campo obbligatorio', `Rispondi alla domanda: "${question.text}"`);
        return false;
      }

      // Validation rules (solo se c'è una risposta)
      if (answer && v) {
        // Text/Textarea: minLength, maxLength
        if (['text', 'textarea'].includes(question.type)) {
          if (v.minLength && answer.length < v.minLength) {
            toast.error('Testo troppo corto', `"${question.text}" richiede almeno ${v.minLength} caratteri`);
            return false;
          }
          if (v.maxLength && answer.length > v.maxLength) {
            toast.error('Testo troppo lungo', `"${question.text}" consente massimo ${v.maxLength} caratteri`);
            return false;
          }
        }

        // Number: minValue, maxValue
        if (question.type === 'number') {
          const num = parseFloat(answer);
          if (!isNaN(num)) {
            if (v.minValue !== undefined && v.minValue !== null && num < v.minValue) {
              toast.error('Valore troppo basso', `"${question.text}" richiede un valore minimo di ${v.minValue}`);
              return false;
            }
            if (v.maxValue !== undefined && v.maxValue !== null && num > v.maxValue) {
              toast.error('Valore troppo alto', `"${question.text}" richiede un valore massimo di ${v.maxValue}`);
              return false;
            }
          }
        }

        // Checkbox: minSelections, maxSelections
        if (question.type === 'checkbox') {
          const selections = answer.split(',').filter(Boolean).length;
          if (v.minSelections && selections < v.minSelections) {
            toast.error('Selezioni insufficienti', `"${question.text}" richiede almeno ${v.minSelections} selezioni`);
            return false;
          }
          if (v.maxSelections && selections > v.maxSelections) {
            toast.error('Troppe selezioni', `"${question.text}" consente massimo ${v.maxSelections} selezioni`);
            return false;
          }
        }
      }
    }
    return true;
  };

  // Errori di saveProgress: identità/territorio con la loro schermata, gli altri col messaggio del server
  const salvaAvanzamento = async (stepIndex: number): Promise<boolean> => {
    try {
      await saveProgress({ variables: { responseId, currentStepIndex: stepIndex, ...identitaVars } });
      return true;
    } catch (error) {
      if (!gestisciErroreBloccante(error, { mutation: true })) toast.error('Errore', (error as Error)?.message || 'Salvataggio non riuscito');
      return false;
    }
  };

  // Lasciando lo step della domanda territoriale (visibile) si rimanda sempre lo stato
  // completo delle province: un salvataggio perso per concorrenza viene recuperato
  const rimandaTerritorio = () => {
    if (isPreview || !responseId || !domandaTerritorio) return;
    if (!currentStep?.questions.some(q => q.questionId === domandaTerritorio.questionId)) return;
    if (!isQuestionVisible(domandaTerritorio)) return;
    const terr = territorioRef.current;
    const risposte = terr ? filtraProvince(territorioAnswers, terr.province) : territorioAnswers;
    territorioInSospeso.current = { questionId: domandaTerritorio.questionId, risposte };
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    // Risposte territoriali (anche nel debounce): prima si salvano
    rimandaTerritorio();
    if (!(await salvaTerritorio())) return;

    // Valuta branching rules
    const branchAction = evaluateStepBranching();

    let nextIndex: number;

    if (branchAction) {
      switch (branchAction.type) {
        case 'go_to_step': {
          const targetIdx = displayedSteps.findIndex(s => s.stepId === branchAction.targetId);
          nextIndex = targetIdx >= 0 ? targetIdx : currentStepIndex + 1;
          break;
        }
        case 'end_survey': {
          if (isPreview) { setIsCompleted(true); return; }
          if (responseId) {
            // Il server considera mai mostrati gli step oltre quello salvato
            // (domanda territoriale non pretesa se il sondaggio finisce prima)
            if (!(await salvaAvanzamento(currentStepIndex))) return;
            await completeResponse({ variables: { responseId, ...identitaVars } });
          }
          return;
        }
        default:
          nextIndex = currentStepIndex + 1;
      }
    } else {
      nextIndex = currentStepIndex + 1;
    }

    if (nextIndex < totalSteps) {
      setCurrentStepIndex(nextIndex);
      setStepHistory(prev => [...prev, nextIndex]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (!isPreview && responseId) await salvaAvanzamento(nextIndex);
    }
  };

  const handlePrevious = () => {
    if (stepHistory.length > 1) {
      const newHistory = [...stepHistory];
      newHistory.pop();
      const previousStep = newHistory[newHistory.length - 1];
      setStepHistory(newHistory);
      setCurrentStepIndex(previousStep);
    } else if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveDraft = async () => {
    if (responseId) {
      if (!(await salvaTerritorio())) return;
      if (!(await salvaAvanzamento(currentStepIndex))) return;
      setDraftSaved(true);
      toast.success('Bozza salvata', 'Puoi riprendere il sondaggio in qualsiasi momento.');
      setTimeout(() => setDraftSaved(false), 3000);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    if (isPreview) { setIsCompleted(true); return; }
    rimandaTerritorio();
    if (!(await salvaTerritorio())) return;
    if (responseId) await completeResponse({ variables: { responseId, ...identitaVars } });
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className={`${classeContenitore} bg-gray-50 dark:bg-gray-900 flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // ── Survey non trovato ──
  if (!survey) {
    return (
      <div className={`${classeContenitore} bg-gray-50 dark:bg-gray-900 flex items-center justify-center`}>
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Sondaggio non trovato</h2>
          <button onClick={() => navigate(percorsi.home)} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            {associativa ? 'Torna alla dashboard' : 'Torna all\'area riservata'}
          </button>
        </div>
      </div>
    );
  }

  // ── Completato (prima dei date check: chi ha già risposto vede "Grazie" anche se il sondaggio è chiuso) ──
  if (isCompleted) {
    // Risposta dello studio inviata da un altro (titolare o collega): niente «Grazie», titolo neutro
    const compilataDaAltri = completataPrima && !!compilatoDa && !compilatoDaMe(compilatoDa);
    return (
      <div className={`${classeContenitore} bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4`}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          {compilataDaAltri ? (
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-6">Sondaggio già compilato per lo studio</h2>
          ) : (
            <>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Grazie!</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{survey.settings.thankYouMessage || 'Grazie per aver completato il sondaggio!'}</p>
            </>
          )}
          {completataPrima && compilatoDa?.nome && (
            <p className="-mt-3 mb-6 text-sm text-gray-500 dark:text-gray-400">
              {`Compilato per lo studio da ${compilatoDa.nome}${dataLunga(responseData?.mySurveyResponse?.completedAt) ? ` il ${dataLunga(responseData?.mySurveyResponse?.completedAt)}` : ''}`}
            </p>
          )}
          {isPreview && (
            <p className="text-amber-600 dark:text-amber-400 text-sm mb-4 font-medium">Modalità Test - Nessuna risposta salvata</p>
          )}
          <button onClick={() => {
            if (isPreview) { window.close(); return; }
            if (survey.settings.redirectUrl) window.location.href = survey.settings.redirectUrl;
            else navigate(percorsi.home);
          }} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">{isPreview ? 'Torna all\'editor' : 'Continua'}</button>
        </div>
      </div>
    );
  }

  // ── Date checks (skip in preview) ──
  const now = new Date();
  const surveyStartDate = survey.startDate ? new Date(survey.startDate) : null;
  const surveyEndDate = survey.endDate ? new Date(survey.endDate) : null;
  const hasNotStarted = !isPreview && surveyStartDate && surveyStartDate > now;
  const hasEnded = !isPreview && surveyEndDate && surveyEndDate < now;

  if (hasNotStarted) {
    const formattedStart = surveyStartDate.toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    const formattedTime = surveyStartDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    return (
      <div className={`${classeContenitore} bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4`}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Sondaggio non ancora disponibile</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-2"><span className="font-semibold">{survey.title}</span></p>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
            Sarà disponibile a partire da:<br />
            <span className="font-semibold text-amber-600 dark:text-amber-400 capitalize">{formattedStart} alle {formattedTime}</span>
          </p>
          <button onClick={() => navigate(percorsi.elenco)} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">Torna ai sondaggi</button>
        </div>
      </div>
    );
  }

  if (hasEnded || (!isPreview && survey.status === 'CLOSED')) {
    return (
      <div className={`${classeContenitore} bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4`}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Sondaggio chiuso</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{survey.settings.closedMessage || 'Questo sondaggio non accetta più risposte.'}</p>
          <button onClick={() => navigate(percorsi.elenco)} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">Torna ai sondaggi</button>
        </div>
      </div>
    );
  }

  // ── Identità richiesta (sondaggi con identità verificata, senza accesso né link valido); dopo «chiuso» e date ──
  if (schermataAccesso && !isPreview) {
    return (
      <div className={`${classeContenitore} bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4`}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
          </div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{survey.title}</h2>
          {schermataAccesso.linkScaduto && (
            <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">Il link è scaduto</p>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
            {associativa
              ? 'Per compilare questo sondaggio accedi all\'area associativa o usa il link ricevuto per email'
              : 'Per compilare questo sondaggio accedi all\'area riservata o usa il link ricevuto per email'}
          </p>
          <button
            onClick={() => navigate('/accesso-associati', { state: { from: percorsi.compila(id || '') } })}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {associativa ? 'Accedi all\'area associativa' : 'Accedi all\'area riservata'}
          </button>
        </div>
      </div>
    );
  }

  // ── Compilazione non possibile (territorio non disponibile, impersonificazione, altro pubblico,
  //    dipendente quando risponde il titolare o senza la sezione): messaggio del server ──
  const messaggioBlocco = bloccoServer || (soloTitolare ? MESSAGGIO_SOLO_TITOLARE : null);
  const codiceBlocco = bloccoServer ? bloccoCodice : (soloTitolare ? 'RISPONDE_IL_TITOLARE' : null);
  if (messaggioBlocco && !isPreview) {
    const azioneBlocco = (codiceBlocco && AZIONI_PUBBLICO[codiceBlocco])
      || (codiceBlocco && CODICI_RAPPRESENTANTE.includes(codiceBlocco)
        ? { etichetta: 'Torna alla dashboard', percorso: percorsi.home }
        : codiceBlocco === 'SEZIONE_NON_ABILITATA'
          ? { etichetta: 'Torna all\'area riservata', percorso: percorsi.home }
          : { etichetta: 'Torna ai sondaggi', percorso: percorsi.elenco });
    return (
      <div className={`${classeContenitore} bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4`}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          </div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{survey.title}</h2>
          {codiceBlocco === 'RISPONDE_IL_TITOLARE' && messaggioBlocco !== 'Risponde il titolare' && (
            <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">Risponde il titolare</p>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">{messaggioBlocco}</p>
          {avvisoInCompilazione && (
            <p className="-mt-3 mb-5 text-xs text-gray-500 dark:text-gray-400">{`In compilazione da ${avvisoInCompilazione}`}</p>
          )}
          <button onClick={() => navigate(azioneBlocco.percorso)} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
            {azioneBlocco.etichetta}
          </button>
        </div>
      </div>
    );
  }

  // ── Welcome screen (welcomeMessage) ──
  if (showWelcome && survey.settings.welcomeMessage) {
    return (
      <div className={`${classeContenitore} bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4`}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-lg w-full">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{survey.title}</h1>
            {survey.description && <p className="text-sm text-gray-500 dark:text-gray-400">{survey.description}</p>}
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-5 mb-6">
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-line">{survey.settings.welcomeMessage}</p>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500 mb-6">
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              {totalSteps} {totalSteps === 1 ? 'step' : 'step'}
            </span>
            {survey.settings.timeLimitMinutes && (
              <span className="flex items-center gap-1 text-amber-500">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {survey.settings.timeLimitMinutes} minuti
              </span>
            )}
          </div>
          <button onClick={() => { setShowWelcome(false); if (survey.settings.timeLimitMinutes) setTimeLeft(survey.settings.timeLimitMinutes * 60); }} className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors">
            Inizia il Sondaggio →
          </button>
        </div>
      </div>
    );
  }

  // ── Render media ──
  const renderMedia = (media: MediaContent | undefined) => {
    if (!media?.url) return null;
    let mediaUrl = media.url;
    if (!mediaUrl.startsWith('http')) {
      const cleanUrl = mediaUrl.replace('/api/uploads', '').replace('/uploads', '');
      mediaUrl = `${UPLOAD_URL}${cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`}`;
    }
    return (
      <div className="mb-4">
        {media.type === 'image' && <img src={mediaUrl} alt={media.caption || 'Media'} className="max-w-full h-auto rounded-lg shadow-md" />}
        {media.type === 'pdf' && (
          <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">
            <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24"><path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/></svg>
            <span className="text-blue-600 dark:text-blue-400">Visualizza PDF</span>
          </a>
        )}
        {media.caption && <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 italic">{media.caption}</p>}
      </div>
    );
  };

  // ── Render question ──
  const renderQuestion = (question: Question) => {
    const value = answers[question.questionId] || '';

    if (question.type === 'html') {
      return <div className="survey-html-content testo-ricco p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" dangerouslySetInnerHTML={{ __html: question.text }} />;
    }

    switch (question.type) {
      case 'radio':
        return (
          <div className="space-y-2">
            {question.options.map(opt => (
              <label key={opt.optionId} className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                <input type="radio" name={question.questionId} value={opt.value} checked={value === opt.value} onChange={(e) => handleAnswerChange(question.questionId, e.target.value)} className="mr-3" />
                <span className="text-gray-700 dark:text-gray-300">{opt.text}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox': {
        const selectedValues = value ? value.split(',') : [];
        return (
          <div className="space-y-2">
            {question.options.map(opt => (
              <label key={opt.optionId} className="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                <input type="checkbox" checked={selectedValues.includes(opt.value)} onChange={(e) => {
                  const newValues = e.target.checked ? [...selectedValues, opt.value] : selectedValues.filter(v => v !== opt.value);
                  handleAnswerChange(question.questionId, newValues.join(','));
                }} className="mr-3 rounded" />
                <span className="text-gray-700 dark:text-gray-300">{opt.text}</span>
              </label>
            ))}
          </div>
        );
      }

      case 'text':
        return (
          <div>
            <input type="text" value={value} onChange={(e) => handleAnswerChangeDebounced(question.questionId, e.target.value)} minLength={question.validation?.minLength} maxLength={question.validation?.maxLength} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder={question.placeholder || 'La tua risposta...'} />
            {question.validation?.maxLength && <p className="text-xs text-gray-400 mt-1 text-right">{(value || '').length}/{question.validation.maxLength}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div>
            <textarea value={value} onChange={(e) => handleAnswerChangeDebounced(question.questionId, e.target.value)} minLength={question.validation?.minLength} maxLength={question.validation?.maxLength} rows={4} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder={question.placeholder || 'La tua risposta...'} />
            {question.validation?.maxLength && <p className="text-xs text-gray-400 mt-1 text-right">{(value || '').length}/{question.validation.maxLength}</p>}
          </div>
        );

      case 'email':
        return <input type="email" value={value} onChange={(e) => handleAnswerChangeDebounced(question.questionId, e.target.value)} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder={question.placeholder || 'email@esempio.it'} />;

      case 'phone':
        return <input type="tel" value={value} onChange={(e) => handleAnswerChangeDebounced(question.questionId, e.target.value)} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder={question.placeholder || '+39 123 456 7890'} />;

      case 'number':
        return <input type="number" value={value} onChange={(e) => handleAnswerChangeDebounced(question.questionId, e.target.value)} min={question.validation?.minValue} max={question.validation?.maxValue} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />;

      case 'scale': {
        const min = question.scaleConfig?.min || 1;
        const max = question.scaleConfig?.max || 5;
        const scaleValues = Array.from({ length: max - min + 1 }, (_, i) => min + i);
        return (
          <div>
            <div className="flex justify-between gap-1 sm:gap-2">
              {scaleValues.map(n => (
                <button key={n} onClick={() => handleAnswerChange(question.questionId, String(n))} className={`flex-1 min-w-[32px] h-10 rounded-lg font-medium transition-colors ${value === String(n) ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>{n}</button>
              ))}
            </div>
            {(question.scaleConfig?.minLabel || question.scaleConfig?.maxLabel) && (
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>{question.scaleConfig?.minLabel}</span>
                <span>{question.scaleConfig?.maxLabel}</span>
              </div>
            )}
          </div>
        );
      }

      case 'nps':
        return (
          <div>
            <div className="flex justify-between gap-1">
              {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                <button key={n} onClick={() => handleAnswerChange(question.questionId, String(n))} className={`flex-1 min-w-[28px] h-10 rounded font-medium text-sm transition-colors ${value === String(n) ? 'ring-2 ring-offset-2 ring-blue-500' : ''} ${n <= 6 ? 'bg-red-100 text-red-700 hover:bg-red-200' : n <= 8 ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>{n}</button>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>Per niente probabile</span><span>Molto probabile</span>
            </div>
          </div>
        );

      case 'slider': {
        const sc = question.sliderConfig || { min: 0, max: 100, step: 1, minLabel: '', maxLabel: '', showValue: true };
        const sliderValue = value || String(Math.round((sc.min + sc.max) / 2));
        return (
          <div className="space-y-3">
            <input type="range" min={sc.min} max={sc.max} step={sc.step} value={sliderValue} onChange={(e) => handleAnswerChangeDebounced(question.questionId, e.target.value)} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-blue-600" />
            <div className="flex justify-between text-sm text-gray-500">
              <span>{sc.minLabel || sc.min}</span>
              {sc.showValue && <span className="font-semibold text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-3 py-0.5 rounded-full">{sliderValue}</span>}
              <span>{sc.maxLabel || sc.max}</span>
            </div>
          </div>
        );
      }

      case 'dropdown':
        return (
          <select value={value} onChange={(e) => handleAnswerChange(question.questionId, e.target.value)} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            <option value="">Seleziona...</option>
            {question.options.map(opt => <option key={opt.optionId} value={opt.value}>{opt.text}</option>)}
          </select>
        );

      case 'ranking': {
        const rankValues = value ? value.split(',') : question.options.map(o => o.value);
        const moveItem = (index: number, direction: 'up' | 'down') => {
          const newArr = [...rankValues];
          const swapIndex = direction === 'up' ? index - 1 : index + 1;
          [newArr[index], newArr[swapIndex]] = [newArr[swapIndex], newArr[index]];
          handleAnswerChange(question.questionId, newArr.join(','));
        };
        return (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 mb-2">Ordina dal più importante al meno importante:</p>
            {rankValues.map((val, idx) => {
              const opt = question.options.find(o => o.value === val);
              return (
                <div key={val} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">{idx + 1}</span>
                  <span className="flex-1 text-gray-700 dark:text-gray-300">{opt?.text || val}</span>
                  <div className="flex gap-1">
                    <button onClick={() => moveItem(idx, 'up')} disabled={idx === 0} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30">↑</button>
                    <button onClick={() => moveItem(idx, 'down')} disabled={idx === rankValues.length - 1} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30">↓</button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      case 'date':
        return <input type="date" value={value} onChange={(e) => handleAnswerChange(question.questionId, e.target.value)} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />;

      case 'time':
        return <input type="time" value={value} onChange={(e) => handleAnswerChange(question.questionId, e.target.value)} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />;

      case 'file':
        return (
          <div className="space-y-2">
            <input type="file" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              handleAnswerChange(question.questionId, file.name);
            }} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
            {value && <p className="text-sm text-green-600">File selezionato: {value}</p>}
          </div>
        );

      case 'signature': {
        const canvasRef = signatureCanvasRef;
        return (
          <div className="space-y-3">
            <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-700 touch-none">
              <canvas ref={canvasRef} width={560} height={160} className="w-full cursor-crosshair"
                onMouseDown={(e) => {
                  const canvas = canvasRef.current; if (!canvas) return;
                  const ctx = canvas.getContext('2d'); if (!ctx) return;
                  const rect = canvas.getBoundingClientRect();
                  const scaleX = canvas.width / rect.width; const scaleY = canvas.height / rect.height;
                  ctx.beginPath(); ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                  ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2; ctx.lineCap = 'round';
                  const onMove = (ev: MouseEvent) => { ctx.lineTo((ev.clientX - rect.left) * scaleX, (ev.clientY - rect.top) * scaleY); ctx.stroke(); };
                  const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); handleAnswerChange(question.questionId, canvas.toDataURL('image/png')); };
                  document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
                }}
                onTouchStart={(e) => {
                  e.preventDefault(); const canvas = canvasRef.current; if (!canvas) return;
                  const ctx = canvas.getContext('2d'); if (!ctx) return;
                  const rect = canvas.getBoundingClientRect();
                  const scaleX = canvas.width / rect.width; const scaleY = canvas.height / rect.height;
                  const touch = e.touches[0]; ctx.beginPath(); ctx.moveTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
                  ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2; ctx.lineCap = 'round';
                  const onTouchMove = (ev: TouchEvent) => { ev.preventDefault(); const t = ev.touches[0]; ctx.lineTo((t.clientX - rect.left) * scaleX, (t.clientY - rect.top) * scaleY); ctx.stroke(); };
                  const onTouchEnd = () => { canvas.removeEventListener('touchmove', onTouchMove); canvas.removeEventListener('touchend', onTouchEnd); handleAnswerChange(question.questionId, canvas.toDataURL('image/png')); };
                  canvas.addEventListener('touchmove', onTouchMove, { passive: false }); canvas.addEventListener('touchend', onTouchEnd);
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">Disegna la tua firma con il mouse o il touch</p>
              <button type="button" onClick={() => {
                const canvas = canvasRef.current; if (!canvas) return;
                const ctx = canvas.getContext('2d'); if (!ctx) return;
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                handleAnswerChange(question.questionId, '');
              }} className="text-xs text-red-500 hover:text-red-700 font-medium">Cancella firma</button>
            </div>
          </div>
        );
      }

      case 'matrix':
      case 'matrix_multi': {
        const mc = question.matrixConfig;
        if (!mc || !mc.rows?.length || !mc.columns?.length) return <p className="text-gray-500 text-center">Configurazione matrice mancante</p>;
        const matrixAnswers: Record<string, string> = value ? (() => { try { return JSON.parse(value); } catch { return {}; } })() : {};
        const handleMatrixChange = (rowId: string, colValue: string) => {
          const updated = { ...matrixAnswers };
          if (question.type === 'matrix') { updated[rowId] = colValue; }
          else {
            const current = (updated[rowId] || '').split(',').filter(Boolean);
            updated[rowId] = current.includes(colValue) ? current.filter(v => v !== colValue).join(',') : [...current, colValue].join(',');
          }
          handleAnswerChange(question.questionId, JSON.stringify(updated));
        };
        return (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-2 text-gray-500 dark:text-gray-400 text-xs font-medium min-w-[120px]"></th>
                  {mc.columns.map(col => <th key={col.colId} className="p-2 text-center text-gray-600 dark:text-gray-400 text-xs font-medium min-w-[60px]">{col.text}</th>)}
                </tr>
              </thead>
              <tbody>
                {mc.rows.map((row, rIdx) => (
                  <tr key={row.rowId} className={rIdx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700/30' : ''}>
                    <td className="p-2 text-gray-700 dark:text-gray-300 text-sm font-medium">{row.text}</td>
                    {mc.columns.map(col => {
                      const isChecked = question.type === 'matrix' ? matrixAnswers[row.rowId] === col.value : (matrixAnswers[row.rowId] || '').split(',').includes(col.value);
                      return (
                        <td key={col.colId} className="p-2 text-center">
                          <input type={question.type === 'matrix' ? 'radio' : 'checkbox'} name={`matrix_${question.questionId}_${row.rowId}`} checked={isChecked} onChange={() => handleMatrixChange(row.rowId, col.value)} className="w-4 h-4 text-blue-600 cursor-pointer" />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      case 'territorio': {
        const territorio = territorioDi(question);
        if (!territorio) {
          return <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento del territorio di competenza…</p>;
        }
        return (
          <TerritorioQuestion
            question={question}
            territorio={territorio}
            value={territorioAnswers}
            onChange={(provincia, v) => handleTerritorioChange(question.questionId, provincia, v)}
            disabled={timeExpired}
          />
        );
      }

      default:
        return <p className="text-gray-500">Tipo di domanda non supportato: {question.type}</p>;
    }
  };

  // ── Timer color ──
  const timerColor = timeLeft !== null
    ? timeLeft > 60 ? 'text-gray-600 dark:text-gray-400' : timeLeft > 30 ? 'text-amber-500' : 'text-red-500 animate-pulse'
    : '';

  // ── Main render ──
  return (
    <div className={`${classeContenitore} bg-gray-50 dark:bg-gray-900 py-8 px-4`}>

      <div className="max-w-2xl mx-auto">
        {/* Banner modalità test */}
        {isPreview && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Modalità Test - Le risposte non vengono salvate</span>
            </div>
            <button onClick={() => window.close()} className="text-xs px-3 py-1 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded hover:bg-amber-300 dark:hover:bg-amber-700">Chiudi test</button>
          </div>
        )}

        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{survey.title}</h1>
              {survey.description && <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">{survey.description}</p>}
            </div>
            {/* Timer (timeLimitMinutes) */}
            {timeLeft !== null && (
              <div className={`flex items-center gap-1.5 font-mono font-semibold text-sm flex-shrink-0 ${timerColor}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {formatTime(timeLeft)}
              </div>
            )}
          </div>

          {/* Progress bar (showProgressBar) */}
          {survey.settings.showProgressBar && totalSteps > 1 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-1">
                <span>Step {currentStepIndex + 1} di {totalSteps}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* Risposta dello studio iniziata da un'altra persona (titolare o dipendente) */}
        {avvisoInCompilazione && !isPreview && (
          <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
            <UserRound className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span>{`In compilazione da ${avvisoInCompilazione}`}</span>
          </div>
        )}

        {/* Current Step */}
        {currentStep && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{currentStep.title}</h2>
            {currentStep.description && <p className="text-gray-600 dark:text-gray-400 mb-4">{currentStep.description}</p>}
            {renderMedia(currentStep.media)}

            <div className="space-y-8">
              {currentStep.questions.map((question, index) => {
                if (!isQuestionVisible(question)) return null;
                return (
                  <div key={question.questionId} className={question.type === 'html' ? 'border-l-4 border-blue-500 pl-4' : ''}>
                    {question.type === 'html' ? (
                      <>{renderMedia(question.media)}{renderQuestion(question)}</>
                    ) : (
                      <>
                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                          {index + 1}. {question.text}
                          {question.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {question.description && <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{question.description}</p>}
                        {renderMedia(question.media)}
                        {renderQuestion(question)}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <button onClick={handlePrevious} disabled={stepHistory.length <= 1}
                  className={`px-6 py-2 rounded-lg font-medium ${stepHistory.length <= 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'}`}>
                  ← Indietro
                </button>

                <div className="flex items-center gap-3">
                  {/* Salva bozza (allowSaveDraft) */}
                  {survey.settings.allowSaveDraft && (
                    <button onClick={handleSaveDraft}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${draftSaved ? 'border-green-400 text-green-600 bg-green-50 dark:bg-green-900/20' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                      {draftSaved ? '✓ Salvato' : '💾 Salva bozza'}
                    </button>
                  )}

                  {currentStepIndex < totalSteps - 1 ? (
                    <button onClick={handleNext} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">
                      Avanti →
                    </button>
                  ) : (
                    <button onClick={handleSubmit} disabled={timeExpired}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg disabled:opacity-50">
                      Invia Risposte
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyResponsePage;
